/**
 * ⭐ СТУПЕНИ 3 И 5 · ПРОПУСК ВЫБОРОМ · окно М3 (28.07) + окно М5 (29.07) — пятый модуль
 * витрины с настоящей ЛОГИКОЙ (после `grammar.ts`, `audio.ts`, `build.ts`, `ladder.ts`).
 * Проверяется запуском:
 *
 *   cd Lang/web && node tools/check-ladder.mjs
 *
 * Что это за задание (методика — `knowledge/methodology/delivery-ladder.md`, ступень 3):
 * человек видит КОРОТКИЙ пример с пропуском и его перевод, а выбирает из 3–4 кнопок —
 * нужная форма, лемма, другие формы слова. Проверяется смысл в контексте И грамматическая
 * форма: `has _____` + `go` не засчитан, как и в «Контексте», только без клавиатуры.
 *
 * ⭐ Почему ступень 3 стала возможна только сейчас: она первая, где работают ПО примеру,
 * а посильный пример был у 15.3% карточек (у новичка — у 0%). Окно Ш5-B это починило:
 * у всех 919 слов активной полосы есть короткий пример без единого незнакомого слова.
 * Ровно об это разбилось окно 3 «Собери фразу» — там просили выложить ВСЁ предложение;
 * решением 28.07 полная сборка урезана до ОДНОГО пропуска, и это он и есть.
 *
 * ⭐⭐ ОТДЕЛЬНАЯ ВЕТКА — СЛУЖЕБНОЕ СЛОВО. У `would` форм нет (в базе там «—» и сам
 * `would`), а спрашивать его переводом нельзя вообще («бы» — обломок, а не перевод).
 * Поэтому кнопками под пропуском идут ДРУГИЕ СЛУЖЕБНЫЕ слова:
 *   «_____ you like some tea?» → **would** · could · should
 * Кто служебный и когда переключаться — решает ЯДРО (`lexicon.function_word_group`,
 * поле карточки `function_word`), витрина только собирает кнопки.
 *
 * ⭐⭐ ТРЕТЬЯ ВЕТКА — ОБОРОТ (окно 1 запуска, 31.07). У конструкции `function_word` честно
 * `null` (у неё есть цельный русский эквивалент), поэтому она попадала в общую ветку и
 * получала кнопками свои же формы и слова соседних карточек:
 *   «I _____ go now.» → have to · had to · water · time
 * А спрашивать надо СМЫСЛ среди себе подобных:
 *   «I _____ go now.» → **have to** · going to · would like · used to
 * Список опять же решает ЯДРО (`lexicon.construction_peers`, поле `construction_peers`) —
 * витрина не хранит ни перечня оборотов, ни правила, кто кому сосед.
 *
 * ⭐⭐ ОКНО М5 (29.07) добавило сюда СТУПЕНЬ 5 «фраза на слух» — `phraseGap` внизу файла.
 * Своей сборки кнопок у неё нет нарочно: это та же ступень 3, только вопрос звучит
 * целиком, а материал длинный. Разница между ступенями — в вопросе, а не в кнопках.
 *
 * ⭐⭐ ОКНО М5.1 (30.07) добавило ОПОРУ ПО ТРЕБОВАНИЮ — `showsPhraseRu` и
 * `opensRuAfterMiss`: перевод фразы на ступени 5 открывается кнопкой (и сам — но только
 * после СМЫСЛОВОГО промаха). Правило живёт здесь, а не в экране, ровно чтобы его можно
 * было проверить запуском.
 *
 * ⛔ Здесь нет ни DOM, ни сети, ни SRS: только строки. Оценку в память кладёт ядро.
 */

import type { Card } from "./api";
import { formList, gapSpec, isBlankForm, norm } from "./grammar";

/** Сколько кнопок хотим на экране. Меньше трёх — это уже не выбор, а подсказка. */
export const WANT_OPTIONS = 4;
export const MIN_OPTIONS = 3;

/**
 * ⭐⭐ М5 · ПОТОЛОК ДЛИНЫ ФРАЗЫ ДЛЯ СТУПЕНИ 5 (закон методики, разд. 3.2: «пример длиннее
 * 12 слов не годится под ступень 5 → даём 3»). Причина не эстетическая: предложение
 * держат в рабочей памяти целиком, пока ищут пропуск, — на длинном человек проверяет
 * не слово, а объём памяти.
 *
 * Замер по боевой базе (29.07): длинных примеров 2869, все озвучены и все с переводом;
 * ступень 5 собирается у **2727 = 95.1%**. Отсеиваются 142: **69 (2.4%)** с двумя
 * пропусками и **73 (2.5%)** длиннее 12 слов — они молча уходят ступенью ниже.
 */
export const HEAR_MAX_WORDS = 12;

/**
 * Служебные слова для кнопок-соседей. Порядок и состав — ровно как в ядре
 * (`lexicon.FUNCTION_WORDS_PURE` / `FUNCTION_WORDS_DUAL`): витрина этот список НЕ решает,
 * она им только заполняет кнопки. Группа приходит полем карточки `function_word`.
 */
export const FUNCTION_PEERS: Record<"A" | "B", string[]> = {
  A: ["would", "will", "should", "could", "might", "must", "shall"],
  B: ["be", "have", "do", "can", "may", "need", "let"],
};

export interface GapSpecChoice {
  /** кусок предложения до пропуска и после него (пропуск ровно один) */
  before: string;
  after: string;
  /** правильный ответ — ИМЕННО та форма, что стоит в предложении */
  correct: string;
  /** что показываем кнопками (правильный + отвлекающие, перемешаны стабильно) */
  options: string[];
  /** перевод примера: без него непонятно, что именно вставлять */
  ru: string;
  /** чем набраны кнопки — от этого зависит подпись задания */
  source: "forms" | "function" | "construction" | "pool";
}

/** Перемешивание с постоянным зерном: перерисовка не двигает кнопки под пальцем. */
export function shuffleSeeded<T>(list: T[], seed: number): T[] {
  const out = list.slice();
  let s = Math.abs(Math.round(seed)) * 9301 + 49297;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Заглавная как у образца: пропуск в начале предложения не должен выдавать ответ. */
function matchCase(value: string, sample: string): string {
  const head = sample.trim()[0] || "";
  if (!/[A-ZА-Я]/.test(head)) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Все написания САМОГО слова: лемма + формы из `lexicon.forms` + формы примера. */
function ownForms(c: Card): string[] {
  const out = [String(c.word || "")];
  for (const v of Object.values(c.forms || {})) if (!isBlankForm(v)) out.push(String(v));
  for (const f of formList(c)) out.push(f);
  return out;
}

/** Другие служебные слова: сперва своей группы, потом соседней — их всегда хватает. */
export function functionPeers(word: string, group: "A" | "B"): string[] {
  const other = group === "A" ? "B" : "A";
  return [...FUNCTION_PEERS[group], ...FUNCTION_PEERS[other]]
    .filter((w) => norm(w) !== norm(word));
}

/**
 * Собирается ли ступень 3 из ГОТОВЫХ данных? Нет — вернём null, и лестница честно
 * отдаст ступень НИЖЕ (закон М1: «нет данных под ступень — берём ступень ниже, молча»).
 *
 * Не собирается в трёх случаях:
 *   • пропусков в заготовке не ровно один — разрывной оборот («was _____ _____ during»,
 *     2.6% коротких примеров) это не ступень 3, а ступень 5;
 *   • нет перевода примера — тогда непонятно, что вставлять;
 *   • не набралось трёх кнопок — выбор из двух ничего не проверяет.
 *
 * `pool` — слова соседних карточек подхода: ими добираем кнопки, когда своих форм у слова
 * нет (существительное без форм — это 61% активной полосы). Тогда задание проверяет смысл
 * в контексте, а не форму, — и то и другое ступень 3 спрашивает по методике.
 */
export function gapChoice(c: Card, pool: string[] = [], want = WANT_OPTIONS): GapSpecChoice | null {
  const spec = gapSpec(c);
  if (!spec || spec.targets.length !== 1) return null;
  const ru = (c.example?.ru || "").trim();
  if (!ru) return null;

  const correct = spec.targets[0].trim();
  const fn = c.function_word ?? null;
  const options = [correct];
  const used = new Set([norm(correct)]);
  const add = (raw: string): void => {
    const t = matchCase(String(raw || "").trim(), correct);
    const n = norm(t);
    if (!n || used.has(n) || options.length >= want) return;
    used.add(n);
    options.push(t);
  };

  // ⭐ У служебного слова кнопками идут ДРУГИЕ СЛУЖЕБНЫЕ (это и есть «конструкция»),
  // ⭐⭐ у ОБОРОТА — другие обороты (окно 1 запуска), у обычного слова — свои формы;
  // хвост в остальных случаях добираем словами подхода.
  const cons = c.construction_peers || [];
  if (fn) functionPeers(c.word || "", fn).forEach(add);
  else if (cons.length) cons.forEach(add);
  if (!cons.length) ownForms(c).forEach(add);
  for (const p of pool) add(p);

  if (options.length < MIN_OPTIONS) return null;
  // Чем набраны кнопки — это подпись задания, а не украшение: «выбери нужную форму»
  // честно только там, где отвлекающие кнопки действительно формы того же слова.
  const others = options.filter((o) => norm(o) !== norm(correct));
  const source: GapSpecChoice["source"] =
    fn ? "function"
      : cons.length ? "construction"
        : others.some((o) => isFormMiss(o, c)) ? "forms" : "pool";
  return {
    before: spec.chunks[0],
    after: spec.chunks[1] ?? "",
    correct,
    options: shuffleSeeded(options, c.word_id || 1),
    ru,
    source,
  };
}

/** Сколько слов в предложении (считаем слова, а не знаки: «I'd» — одно, «—» — ноль). */
export function wordCount(text: string): number {
  return (String(text || "").match(/[A-Za-z’'-]+/g) || []).length;
}

/**
 * ⭐⭐ СТУПЕНЬ 5 «ФРАЗА НА СЛУХ» (окно М5) — старший брат ступени 3.
 *
 * Задание то же самое (один пропуск, ответ кнопкой, сверка ПО ФОРМЕ) и собирается ТЕМ ЖЕ
 * `gapChoice` — новой логики выбора кнопок здесь нет и быть не должно: разница между
 * ступенями 3 и 5 не в кнопках, а в том, ЧЕМ задан вопрос. На ступени 3 предложение
 * читают глазами вместе с переводом; на ступени 5 оно ЗВУЧИТ целиком, а перевод
 * приходит только в разборе — иначе ухо не работает, работает чтение.
 *
 * Отличий от ступени 3 ровно два, и оба — сужения:
 *   • материал ДЛИННЫЙ (вторая ось лестницы; его выдаёт ядро само с бокса 4 —
 *     `lexicon.SHORT_MAX_BOX = 3`), поэтому нужен потолок длины;
 *   • нужен файл озвучки ФРАЗЫ — его наличие проверяет экран занятия (плеер), сюда оно
 *     не приходит: этот файл чистый, без сети.
 *
 * Не собралось — null, и лестница честно отдаёт ступень 4 (закон М1 «нет данных под
 * ступень — берём ступень ниже, молча»).
 */
export function phraseGap(c: Card, pool: string[] = [], want = WANT_OPTIONS): GapSpecChoice | null {
  const spec = gapChoice(c, pool, want);
  if (!spec) return null;
  const words = wordCount(`${spec.before} ${spec.correct} ${spec.after}`);
  return words <= HEAR_MAX_WORDS ? spec : null;
}

/**
 * ⭐⭐ М5.1 (30.07) · ОПОРА ПО ТРЕБОВАНИЮ — ПОКАЗЫВАТЬ ЛИ ПЕРЕВОД ФРАЗЫ.
 *
 * Методика, разд. 3.6: трудность ступени держим в ЗАДАНИИ, а опору даём КНОПКОЙ —
 * видимой, необязательной и без штрафа. На ступени 3 перевод и ЕСТЬ задание (он говорит,
 * что вставлять), поэтому там он стоит всегда. На ступени 5 задание — услышать: покажи
 * перевод сразу, и упражнение решается чтением, то есть ступень схлопывается в ступень 3
 * со звуком. Но и держать человека без опоры нельзя — замер 30.07: у **71.5%** длинных
 * примеров (2003 из 2801) есть хотя бы одно слово выше ранга целевого, у 36.3% — два и
 * больше; без опоры две трети карточек превращаются в лотерею.
 *
 * `asked` — человек нажал «не понял фразу — показать перевод». ⛔ Ноль за это не ставим и
 * ступень не понижаем: опора ничего не проверяет, она не даёт упереться в стену.
 */
export function showsPhraseRu(hearing: boolean, settled: boolean, asked: boolean): boolean {
  if (!hearing) return true;          // ступень 3: перевод — это само задание
  return settled || asked;            // ступень 5: в разборе всегда, до него — по требованию
}

/**
 * ⭐ М5.1 · ОТКРЫВАЕТСЯ ЛИ ПЕРЕВОД САМ ПОСЛЕ ПЕРВОГО ПРОМАХА.
 *
 * Только после СМЫСЛОВОГО промаха («не то слово»): человек не понял фразу, и вторую
 * попытку без опоры он тратит вслепую. ⛔ После промаха ПО ФОРМЕ — нет: там перевод прямо
 * ВРЁТ. «What did you \_\_\_\_\_ to her last night?» = «Что ты **сказал** ей вчера
 * вечером?» — русское прошедшее толкает к `said`, а после `did` идёт `say`. Опора, которая
 * на этом задании врёт, не должна открываться сама (методика, разд. 3.6).
 */
export function opensRuAfterMiss(hearing: boolean, formMiss: boolean): boolean {
  return hearing && !formMiss;
}

/**
 * Сверка. Придираемся ровно к тому же, к чему «Контекст»: нужна ИМЕННО та форма, что
 * стоит в предложении. Регистр и оформление не считаются ошибкой — их задаёт кнопка.
 */
export function checkChoice(picked: string, spec: GapSpecChoice): boolean {
  return !!picked && norm(picked) === norm(spec.correct);
}

/** Это форма того же слова (промах «слово верное — форма другая»)? */
export function isFormMiss(picked: string, c: Card): boolean {
  return ownForms(c).some((f) => norm(f) === norm(picked));
}
